<script setup lang="ts">
  import { z } from 'zod';
  import { Achievement, AchievementType } from '@borg/types';
  import { MpDatePicker, MpInput, MpTextArea, pushToastMessage } from '@borg/ui';
  import { AchievementEditModalConfig, EditModalEvents } from './types';
  import { isNumber } from '@borg/utils';

  const props = defineProps<{
    isOpen: boolean;
    type: AchievementType;
    id?: number | string;
  }>();

  const emits = defineEmits<EditModalEvents>();

  const { t } = useI18n();
  const inputRef = ref<InstanceType<typeof MpInput> | null>(null);

  const CONFIG: AchievementEditModalConfig =
    props.type === 'awards'
      ? {
          TITLE: t('MY_PROFILE_PAGE.AWARD_MODAL.TITLE'),
          FIELDS: {
            'id': null,
            'name': {
              LABEL: t('MY_PROFILE_PAGE.AWARD_MODAL.AWARD_LABEL'),
            },
            'description': {
              LABEL: t('MY_PROFILE_PAGE.AWARD_MODAL.DESCRIPTION_LABEL'),
            },
            'startedOn': {
              LABEL: t('MY_PROFILE_PAGE.AWARD_MODAL.YEAR_LABEL'),
              PICK: 'year',
            },
            'endedOn': null,
          },
        }
      : props.type === 'bibliography'
        ? {
            TITLE: t('MY_PROFILE_PAGE.BIBLIOGRAPHY_MODAL.TITLE'),
            FIELDS: {
              'id': null,
              'name': {
                LABEL: t('MY_PROFILE_PAGE.BIBLIOGRAPHY_MODAL.BIBLIOGRAPHY_LABEL'),
              },
              'description': null,
              'startedOn': {
                LABEL: t('MY_PROFILE_PAGE.BIBLIOGRAPHY_MODAL.YEAR_LABEL'),
                PICK: 'year',
              },
              'endedOn': null,
            },
          }
        : props.type === 'conferences'
          ? {
              TITLE: t('MY_PROFILE_PAGE.CONFERENCE_MODAL.TITLE'),
              FIELDS: {
                'id': null,
                'name': {
                  LABEL: t('MY_PROFILE_PAGE.CONFERENCE_MODAL.CONFERENCE_LABEL'),
                },
                'description': {
                  LABEL: t('MY_PROFILE_PAGE.CONFERENCE_MODAL.DESCRIPTION_LABEL'),
                },
                'startedOn': {
                  LABEL: t('MY_PROFILE_PAGE.CONFERENCE_MODAL.YEAR_LABEL'),
                  PICK: 'year',
                },
                'endedOn': null,
              },
            }
          : {
              TITLE: t('MY_PROFILE_PAGE.PROJECT_MODAL.TITLE'),
              FIELDS: {
                'id': null,
                'name': {
                  LABEL: t('MY_PROFILE_PAGE.PROJECT_MODAL.PROJECT_LABEL'),
                },
                'description': {
                  LABEL: t('MY_PROFILE_PAGE.PROJECT_MODAL.DESCRIPTION_LABEL'),
                },
                'startedOn': {
                  LABEL: t('MY_PROFILE_PAGE.PROJECT_MODAL.START_DATE_LABEL'),
                  PICK: 'month',
                },
                'endedOn': {
                  LABEL: t('MY_PROFILE_PAGE.PROJECT_MODAL.END_DATE_LABEL'),
                  PICK: 'month',
                },
              },
            };

  const { values, state, validate, resetStates, setValues } = useForm({
    initialData: () => transformEntity(),
    schema: z.object({
      name: z.string().min(1, t('REQUIRED_INPUT')),
      startedOn: z.date().nullable(),
      endedOn: z.date().nullable(),
      description: z.string().nullable(),
    }),
  });

  const submitInProgress = ref(false);
  const getInProgress = ref(false);
  const isAwaitingResponse = computed(() => submitInProgress.value || getInProgress.value);

  function transformEntity(input?: Partial<Achievement>) {
    return {
      name: input?.name ?? '',
      startedOn: toDate(input?.startedOn),
      endedOn: toDate(input?.endedOn),
      description: input?.description ?? '',
    };
  }

  async function onModalOpened() {
    if (props.id) {
      getInProgress.value = true;
      const { data: achievement, status } = await achievementsRepository.get(props.id);
      if (status.value === 'success' && achievement.value) {
        setValues(transformEntity(achievement.value));
        getInProgress.value = false;
      } else {
        pushToastMessage({
          type: 'alert',
          subtitle: t('MY_PROFILE_PAGE.FETCH_ERROR_TOAST.SUBTITLE'),
        });
        onClose();
      }
    }
  }

  async function onSubmit() {
    const valid = await validate();
    if (valid) {
      const _values = {
        ...values,
        startedOn: getLocaleDate(values.startedOn),
        endedOn: values.endedOn ? getLocaleDate(values.endedOn) : null,
      };
      try {
        submitInProgress.value = true;
        props.id && isNumber(props.id)
          ? await achievementsRepository.save({ ..._values, id: props.id })
          : await achievementsRepository.create({ ..._values, type: props.type });
        submitInProgress.value = false;
        emits('change');
        onClose();
      } catch (e) {
        submitInProgress.value = false;
        pushToastMessage({
          type: 'alert',
          subtitle: t('MY_PROFILE_PAGE.SUBMIT_ERROR_TOAST.SUBTITLE'),
        });
      }
    }
  }

  function onClose() {
    resetStates();
    emits('update:isOpen', false);
  }
</script>

<template>
  <SiteActionsModal
    :is-open="isOpen"
    :title="CONFIG.TITLE"
    :in-progress="isAwaitingResponse"
    @open="onModalOpened"
    @confirm="onSubmit"
    @cancel="onClose"
    @opened="inputRef?.focus()"
  >
    <MyProfileEditModalForm>
      <MpInput
        v-if="!!CONFIG.FIELDS.name"
        ref="inputRef"
        v-model="values.name"
        :disabled="isAwaitingResponse"
        class="form__row-full"
        :label="CONFIG.FIELDS.name?.LABEL || ''"
        :autofocus="!values.name"
        :error="state.name.message"
        required
      />
      <span
        v-if="!!CONFIG.FIELDS.startedOn"
        :class="!!CONFIG.FIELDS.endedOn ? 'form__row-half' : 'form__row-full'"
      >
        <MpDatePicker
          v-model="values.startedOn"
          :disabled="isAwaitingResponse"
          :label="CONFIG.FIELDS.startedOn?.LABEL || ''"
          :error="state.startedOn.message"
          :max-date="new Date()"
          :pick="CONFIG.FIELDS.startedOn?.PICK"
          clearable
        />
      </span>
      <span
        v-if="!!CONFIG.FIELDS.endedOn"
        :class="!!CONFIG.FIELDS.startedOn ? 'form__row-half' : 'form__row-full'"
      >
        <MpDatePicker
          v-model="values.endedOn"
          :disabled="isAwaitingResponse"
          :label="CONFIG.FIELDS.endedOn?.LABEL || ''"
          :error="state.endedOn.message"
          :max-date="new Date()"
          :pick="CONFIG.FIELDS.startedOn?.PICK"
          clearable
        />
      </span>
      <span
        v-if="!!CONFIG.FIELDS.description"
        class="form__row-full"
      >
        <MpTextArea
          v-model="values.description"
          :disabled="isAwaitingResponse"
          :label="CONFIG.FIELDS.description?.LABEL || ''"
          :error="state.description.message"
          rows="6"
        />
      </span>
    </MyProfileEditModalForm>
  </SiteActionsModal>
</template>
